import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useQuery } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import {
    Badge,
    Button,
    Input,
    Select,
    SelectOption,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCellProps,
    TableRow,
} from '@freelancelabs/components';
import { EyeOutline, MagnifyingGlass } from '@freelancelabs/icons';
import { SORT_DIRECTION } from '@freelancelabs/shared';

import ListCard, { WRAPPING_HEADER } from '../../components/ListCard';
import { AccountUsersSort, MembershipDisplayStatus, SortDirection } from '../../graphql/__generated__/schema';
import { AccessibleAccountsDocument, AccountUsersDocument, RolesDocument } from '../../graphql/__generated__/types';
import FilterChips, { Chip } from './FilterChips';
import { SEVERAL, accountsCell, roleCell, somewhereNotice } from './aggregate';
import { EMPTY, formatDate } from './display';
import { STATUS_FILTER_ORDER, statusCategory, statusLabel } from './status';

const PAGE_SIZE = 25;

/** Long enough not to query on every keystroke, short enough not to feel laggy. */
const SEARCH_DEBOUNCE_MS = 300;

type StatusFilter = MembershipDisplayStatus | 'ALL';
type AccountFilter = string | 'ALL';

/**
 * Portal users, list view. The account is a filter, not a path segment, with options from
 * `accessibleAccounts` (bounded like the list). Sorting and paging are the server's. One row per
 * person, not per membership: the account cell enumerates with a « +n » and the role/status
 * columns say « plusieurs » when memberships disagree. Two things the data cannot say and this
 * screen must (see `aggregate.ts`): the « +n » counts the accounts the reader may see, and the
 * role and status filters mean « somewhere ».
 */
const UsersPage = () => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [status, setStatus] = useState<StatusFilter>('ALL');
    const [account, setAccount] = useState<AccountFilter>('ALL');
    const [sort, setSort] = useState<AccountUsersSort>(AccountUsersSort.LastName);
    const [direction, setDirection] = useState<SortDirection>(SortDirection.Asc);
    const [page, setPage] = useState(0);

    // The role filter lives in the URL, not the toolbar: it is where « N users » of the roles list
    // lands, kept in the address so the link is shareable. Removing its chip removes it from the URL.
    const [searchParams, setSearchParams] = useSearchParams();
    const roleId = searchParams.get('role');

    const { data: accountsData } = useQuery(AccessibleAccountsDocument);
    const accounts = accountsData?.accessibleAccounts ?? [];

    // Only to name the role in its chip. Same query as the roles screens, so Apollo
    // answers from cache as soon as one of them has been opened.
    const { data: rolesData } = useQuery(RolesDocument, { skip: !roleId });
    const roleName = [...(rolesData?.portal ?? []), ...(rolesData?.backoffice ?? [])].find(
        (candidate) => candidate?.id === roleId
    )?.name;

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    // Any change of criteria resets to the first page.
    useEffect(() => setPage(0), [debouncedSearch, status, account, roleId, sort, direction]);

    const { data, loading, error } = useQuery(AccountUsersDocument, {
        variables: {
            // Null and not omitted: the absence of an account is what asks for every account.
            organizationId: account === 'ALL' ? null : account,
            search: debouncedSearch || null,
            status: status === 'ALL' ? null : status,
            roleId,
            sort,
            direction,
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
        },
    });

    const statusOptions: SelectOption<StatusFilter>[] = [
        { label: c('Users filter').t`All statuses`, value: 'ALL' },
        ...STATUS_FILTER_ORDER.map((value) => ({ label: statusLabel(value), value })),
    ];

    const accountOptions: SelectOption<AccountFilter>[] = [
        { label: c('Users filter').t`All accounts`, value: 'ALL' },
        ...accounts.filter(Boolean).map((option) => ({ label: option.name ?? '', value: option.id ?? '' })),
    ];

    /** A sortable header; clicking the sorted column flips it. */
    const sortable = (column: AccountUsersSort): Partial<TableHeaderCellProps> => ({
        direction:
            sort === column ? (direction === SortDirection.Asc ? SORT_DIRECTION.ASC : SORT_DIRECTION.DSC) : undefined,
        onSort: (next: SORT_DIRECTION) => {
            setSort(column);
            setDirection(next === SORT_DIRECTION.ASC ? SortDirection.Asc : SortDirection.Desc);
        },
    });

    const columns: TableHeaderCellProps[] = (
        [
            {
                id: 'lastName',
                label: c('Users column').t`Last name`,
                minWidth: 95,
                ...sortable(AccountUsersSort.LastName),
            },
            { id: 'firstName', label: c('Users column').t`First name`, minWidth: 80 },
            { id: 'email', label: c('Users column').t`Email`, minWidth: 165 },
            { id: 'role', label: c('Users column').t`Role`, minWidth: 130 },
            {
                id: 'organization',
                label: c('Users column').t`Account`,
                minWidth: 180,
                // The enumeration is bounded by the actor's perimeter, so two operators can read a
                // different « +n » for the same person. Said on the header, not on every row.
                textHelp: c('Users column')
                    .t`Only the accounts you are allowed to see are listed and counted here. Someone with a wider portfolio may see more accounts for the same person — that is expected, not an inconsistency.`,
            },
            { id: 'status', label: c('Users column').t`Status`, minWidth: 105 },
            {
                id: 'invitedAt',
                label: c('Users column').t`Invited on`,
                minWidth: 105,
                ...sortable(AccountUsersSort.InvitedAt),
            },
            {
                id: 'activatedAt',
                label: c('Users column').t`Account created`,
                minWidth: 105,
                ...sortable(AccountUsersSort.ActivatedAt),
            },
            { id: 'actions', label: '', minWidth: 150, textAlign: 'end' },
        ] as TableHeaderCellProps[]
    ).map((column) => ({ ...column, className: WRAPPING_HEADER }));

    // `!` and not a plain utility: @freelancelabs/styles is imported after Tailwind in index.scss,
    // so at equal specificity the design system wins and the class would do nothing.
    const wrapping = '!whitespace-normal !break-words py-2';

    const result = data?.accountUsers;
    const items = result?.items ?? [];
    const total = result?.total ?? 0;
    const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
    // ttag refuses an expression inside a localized template, so compute the numbers here.
    const currentPageNumber = page + 1;
    const pageCount = lastPage + 1;

    const filtered = Boolean(debouncedSearch) || status !== 'ALL' || account !== 'ALL' || Boolean(roleId);

    const clearRole = () => setSearchParams({}, { replace: true });

    const clearFilters = () => {
        setSearch('');
        setDebouncedSearch('');
        setStatus('ALL');
        setAccount('ALL');
        clearRole();
    };

    // Said only while one of the two « somewhere » filters is on: see `aggregate.ts`.
    const somewhere = somewhereNotice(Boolean(roleId), status !== 'ALL');

    const chips: Chip[] = [
        ...(debouncedSearch
            ? [
                  {
                      id: 'search',
                      label: c('Users filter').t`Search`,
                      value: debouncedSearch,
                      onRemove: () => {
                          setSearch('');
                          setDebouncedSearch('');
                      },
                  },
              ]
            : []),
        ...(status !== 'ALL'
            ? [
                  {
                      id: 'status',
                      label: c('Users filter').t`Status`,
                      value: statusLabel(status),
                      onRemove: () => setStatus('ALL'),
                  },
              ]
            : []),
        ...(roleId
            ? [
                  {
                      id: 'role',
                      label: c('Users filter').t`Role`,
                      // The name once the referential is there, the identifier meanwhile.
                      value: roleName ?? roleId,
                      onRemove: clearRole,
                  },
              ]
            : []),
        ...(account !== 'ALL'
            ? [
                  {
                      id: 'account',
                      label: c('Users filter').t`Account`,
                      value: accountOptions.find((option) => option.value === account)?.label ?? account,
                      onRemove: () => setAccount('ALL'),
                  },
              ]
            : []),
    ];

    return (
        <section className="flex flex-col gap-5">
            <header className="flex flex-wrap items-start gap-4">
                <div>
                    <h1 className="text-xl font-semibold text-text-900">{c('Users').t`Users`}</h1>
                </div>
                <Link to="/utilisateurs/nouveau" className="ml-auto">
                    <Button category="default">{c('Users').t`Onboard a user`}</Button>
                </Link>
            </header>

            {error ? (
                <p className="text-danger-600" role="alert">
                    {c('Users').t`This list could not be loaded.`}
                </p>
            ) : (
                <ListCard
                    label={c('Users').t`Users of the account`}
                    toolbar={
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-wrap items-center gap-3">
                                <Input
                                    value={search}
                                    onValue={setSearch}
                                    clearable
                                    prefix={<MagnifyingGlass size={16} />}
                                    placeholder={c('Users filter').t`Name, email or job title`}
                                    aria-label={c('Users filter').t`Search a user`}
                                    className="max-w-sm"
                                />
                                <Select
                                    options={statusOptions}
                                    value={statusOptions.find((option) => option.value === status)}
                                    onChange={(option) => setStatus(option.value)}
                                    className="max-w-xs"
                                />
                                {/* Only when there is a choice: a one-option filter reads as broken. */}
                                {accounts.length > 1 && (
                                    <Select
                                        options={accountOptions}
                                        value={accountOptions.find((option) => option.value === account)}
                                        onChange={(option) => setAccount(option.value)}
                                        className="max-w-xs"
                                    />
                                )}
                            </div>
                            <FilterChips chips={chips} onClearAll={clearFilters} />
                            {somewhere && <p className="text-xs text-text-600">{somewhere}</p>}
                        </div>
                    }
                    footer={
                        !loading &&
                        items.length > 0 && (
                            <>
                                {/* « matching » is inside the plural, not appended: a fragment
                                    concatenated outside `ngettext` cannot agree with the count. */}
                                <span>
                                    {filtered
                                        ? c('Users list').ngettext(
                                              msgid`${total} matching user`,
                                              `${total} matching users`,
                                              total
                                          )
                                        : c('Users list').ngettext(msgid`${total} user`, `${total} users`, total)}
                                </span>
                                {total > PAGE_SIZE && (
                                    <nav
                                        className="ml-auto flex items-center gap-3"
                                        aria-label={c('Users').t`Pagination`}
                                    >
                                        <Button
                                            category="weak"
                                            size="small"
                                            disabled={page === 0}
                                            onClick={() => setPage((current) => current - 1)}
                                        >
                                            {c('Action').t`Previous`}
                                        </Button>
                                        <span className="font-mono text-xs">
                                            {c('Pagination').t`Page ${currentPageNumber} of ${pageCount}`}
                                        </span>
                                        <Button
                                            category="weak"
                                            size="small"
                                            disabled={page >= lastPage}
                                            onClick={() => setPage((current) => current + 1)}
                                        >
                                            {c('Action').t`Next`}
                                        </Button>
                                    </nav>
                                )}
                            </>
                        )
                    }
                >
                    <>
                        <Table>
                            <TableHeader resizable cells={columns} />
                            <TableBody loading={loading} loadingRows={5} loadingColumns={columns.length}>
                                {items.map((user) => {
                                    const accounts = accountsCell(user);
                                    const role = roleCell(user);
                                    return (
                                        <TableRow key={user.userId}>
                                            <TableCell className={wrapping}>
                                                <span className="font-semibold">{user.lastName ?? EMPTY}</span>
                                            </TableCell>
                                            <TableCell className={wrapping}>{user.firstName ?? EMPTY}</TableCell>
                                            <TableCell className={`${wrapping} font-mono text-xs`}>
                                                {user.email}
                                            </TableCell>
                                            <TableCell className={wrapping}>
                                                {/* « Plusieurs » is toned down: it is the absence of a
                                                single answer, not a role of that name. */}
                                                <Badge category={role.aggregated ? 'default' : 'info'} size="small">
                                                    {role.label}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className={wrapping}>
                                                {accounts.first}
                                                {accounts.surplus > 0 && (
                                                    <span
                                                        className="ml-1 cursor-help rounded border border-border-norm bg-background-weak px-1 text-xs text-text-600"
                                                        title={accounts.othersTitle}
                                                        tabIndex={0}
                                                    >
                                                        +{accounts.surplus}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {user.status ? (
                                                    <Badge category={statusCategory(user.status)} size="small">
                                                        {statusLabel(user.status)}
                                                    </Badge>
                                                ) : (
                                                    <Badge category="default" size="small">
                                                        {SEVERAL()}
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="font-mono text-xs">
                                                {formatDate(user.invitedAt)}
                                            </TableCell>
                                            <TableCell className="font-mono text-xs">
                                                {/* An empty cell would read as « unknown »; nobody
                                                activated is a state. */}
                                                {user.activatedAt ? (
                                                    formatDate(user.activatedAt)
                                                ) : (
                                                    <span className="font-sans text-text-500">
                                                        {c('Users').t`Not activated`}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-end">
                                                <Button
                                                    to={`/utilisateurs/${user.userId}`}
                                                    category="weak"
                                                    size="small"
                                                    icon
                                                    aria-label={c('Users').t`View the user`}
                                                    title={c('Users').t`View the user`}
                                                >
                                                    <EyeOutline size={16} />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>

                        {!loading && items.length === 0 && (
                            <div className="fl-table-empty flex flex-col items-center gap-3" aria-live="polite">
                                {filtered ? (
                                    <>
                                        <p>{c('Users').t`No user matches these criteria.`}</p>
                                        <p className="text-xs text-text-600">
                                            {c('Users').t`Widen the criteria: remove a filter or shorten the search.`}
                                        </p>
                                        <Button category="weak" size="small" onClick={clearFilters}>
                                            {c('Users').t`Clear filters`}
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <p>{c('Users').t`No user on the accounts you can see.`}</p>
                                        <p className="text-xs text-text-600">
                                            {c('Users')
                                                .t`Onboarding the first user will give them access to the portal.`}
                                        </p>
                                    </>
                                )}
                            </div>
                        )}
                    </>
                </ListCard>
            )}
        </section>
    );
};

export default UsersPage;
